// Task 6: full-page device-user reconciliation for one controlid reader
// (previously a Dialog opened from AccessReadersSection — see
// access-readers-section.tsx). Mirrors CoworkersPage's layout (PageHeader +
// Breadcrumbs, LayerCard + Table, client-side search) and lifts the mapping
// logic (Select pre-set from suggestedCoworkerId, Map button, toast +
// refetch) from device-reconcile-panel.tsx, adding a Photo column that
// lazily fetches each device user's enrolled image.
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Combobox,
  Empty,
  InputGroup,
  LayerCard,
  Loader,
  Table,
  useKumoToastManager,
} from '@cloudflare/kumo'
import {
  ArrowClockwiseIcon,
  BuildingsIcon,
  HouseIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  XIcon,
} from '@phosphor-icons/react'
import { ApiError } from '../api/client'
import {
  getReaderDeviceUserImage,
  listReaderDeviceUsers,
  mapReaderDeviceUser,
  type ReaderDeviceUser,
} from '../api/accessReaders'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'

type StatusFilter = 'all' | 'mapped' | 'unmapped'

interface CoworkerOption {
  id: string
  name: string
}

interface StatusFilterOption {
  value: StatusFilter
  label: string
}

/**
 * Coworker display label. The device-users endpoint only returns `{id,
 * name}` (no email) — falls back to the id when name is blank rather than
 * inventing an email that isn't available client-side.
 */
function coworkerLabel(c: CoworkerOption): string {
  return c.name.trim() !== '' ? c.name : c.id
}

export function DeviceUsersPage() {
  const { buildingId = '', readerId = '' } = useParams<{
    buildingId: string
    readerId: string
  }>()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [deviceUsers, setDeviceUsers] = useState<ReaderDeviceUser[]>([])
  const [coworkers, setCoworkers] = useState<{ id: string; name: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [selections, setSelections] = useState<Record<number, string>>({})
  const [mappingId, setMappingId] = useState<number | null>(null)
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({})
  const [photos, setPhotos] = useState<Record<number, string | null>>({})

  // Tracks every object URL we've handed to the browser so unmount (and each
  // refetch) can revoke exactly the ones we created — no leaks.
  const objectUrlsRef = useRef<Set<string>>(new Set())

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const result = await listReaderDeviceUsers(buildingId, readerId, token)
      setDeviceUsers(result.deviceUsers)
      setCoworkers(result.coworkers)
      // Pre-select the suggested coworker for unmapped users. Preserves any
      // selection the admin already made (e.g. across a re-fetch after a
      // partial mapping) rather than clobbering it back to the suggestion.
      setSelections((prev) => {
        const next = { ...prev }
        for (const du of result.deviceUsers) {
          if (!du.mapped && du.suggestedCoworkerId && next[du.deviceUserId] === undefined) {
            next[du.deviceUserId] = du.suggestedCoworkerId
          }
        }
        return next
      })
      setError(null)

      // Fetch each device user's enrolled photo. Fetched separately from the
      // list (the API doesn't inline images) and turned into object URLs;
      // any previous batch is revoked first so a refetch (e.g. after a
      // successful map) never leaks the URLs it's replacing.
      const entries = await Promise.all(
        result.deviceUsers.map(async (du) => {
          try {
            const blob = await getReaderDeviceUserImage(
              buildingId,
              readerId,
              du.deviceUserId,
              token,
            )
            return [du.deviceUserId, blob ? URL.createObjectURL(blob) : null] as const
          } catch {
            return [du.deviceUserId, null] as const
          }
        }),
      )
      for (const url of objectUrlsRef.current) {
        URL.revokeObjectURL(url)
      }
      objectUrlsRef.current.clear()
      const nextPhotos: Record<number, string | null> = {}
      for (const [deviceUserId, url] of entries) {
        nextPhotos[deviceUserId] = url
        if (url) objectUrlsRef.current.add(url)
      }
      setPhotos(nextPhotos)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'))
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` is stable
    // in the real i18n provider (memoized per locale); omitted here so a
    // test double that returns a fresh `t` reference per render can't spin
    // this callback (and the effect below) into a refetch loop. Mirrors
    // device-reconcile-panel.tsx / face-photo-card.tsx.
  }, [buildingId, readerId, getAccessTokenSilently])

  useEffect(() => {
    void load()
  }, [load])

  // Revoke every object URL we ever created when the page unmounts.
  useEffect(() => {
    return () => {
      for (const url of objectUrlsRef.current) {
        URL.revokeObjectURL(url)
      }
      objectUrlsRef.current.clear()
    }
  }, [])

  // Maps one device user without refetching the whole list — only that
  // row's local state is updated on success, so photos and every other
  // row's in-flight selection/pending state are left untouched.
  async function handleMap(deviceUserId: number) {
    const coworkerId = selections[deviceUserId]
    if (!coworkerId) return
    setMappingId(deviceUserId)
    setRowErrors((prev) => {
      if (!(deviceUserId in prev)) return prev
      const next = { ...prev }
      delete next[deviceUserId]
      return next
    })
    try {
      const token = await getAccessTokenSilently()
      await mapReaderDeviceUser(buildingId, readerId, deviceUserId, coworkerId, token)
      setDeviceUsers((prev) =>
        prev.map((du) => (du.deviceUserId === deviceUserId ? { ...du, mapped: true } : du)),
      )
      toasts.add({ variant: 'success', title: t('accessReaders.deviceUsers.mapSuccess') })
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('common.error')
      setRowErrors((prev) => ({ ...prev, [deviceUserId]: message }))
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: message,
      })
    } finally {
      setMappingId(null)
    }
  }

  const sortedDeviceUsers = [...deviceUsers].sort((a, b) =>
    a.deviceName.localeCompare(b.deviceName),
  )

  const normalizedQuery = query.trim().toLowerCase()
  const filteredDeviceUsers = sortedDeviceUsers.filter((du) => {
    if (normalizedQuery !== '' && !du.deviceName.toLowerCase().includes(normalizedQuery)) {
      return false
    }
    if (statusFilter === 'mapped' && !du.mapped) return false
    if (statusFilter === 'unmapped' && du.mapped) return false
    return true
  })

  const sortedCoworkers = [...coworkers].sort((a, b) =>
    coworkerLabel(a).localeCompare(coworkerLabel(b)),
  )

  const statusFilterOptions: StatusFilterOption[] = [
    { value: 'all', label: t('accessReaders.deviceUsers.filter.all') },
    { value: 'mapped', label: t('accessReaders.deviceUsers.mapped') },
    { value: 'unmapped', label: t('accessReaders.deviceUsers.unmapped') },
  ]
  const selectedStatusOption =
    statusFilterOptions.find((o) => o.value === statusFilter) ?? statusFilterOptions[0]

  function coworkerNameFor(id: string | undefined): string | undefined {
    if (!id) return undefined
    return coworkers.find((c) => c.id === id)?.name
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<BuildingsIcon size={14} />} href="/buildings">
              {t('buildings.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('accessReaders.deviceUsers.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('accessReaders.deviceUsers.title')}
        description={t('accessReaders.deviceUsers.description')}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <InputGroup className="w-full max-w-xs">
          <InputGroup.Addon>
            <MagnifyingGlassIcon />
          </InputGroup.Addon>
          <InputGroup.Input
            value={query}
            placeholder={t('accessReaders.deviceUsers.searchPlaceholder')}
            aria-label={t('accessReaders.deviceUsers.searchPlaceholder')}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query !== '' && (
            <InputGroup.Addon align="end" className="pr-1">
              <InputGroup.Button
                shape="square"
                icon={XIcon}
                aria-label={t('common.clear')}
                onClick={() => setQuery('')}
              />
            </InputGroup.Addon>
          )}
        </InputGroup>

        <Combobox
          items={statusFilterOptions}
          value={selectedStatusOption}
          onValueChange={(v) => {
            const opt = v as StatusFilterOption | null
            setStatusFilter(opt?.value ?? 'all')
          }}
          isItemEqualToValue={(a, b) => a.value === b.value}
        >
          <Combobox.TriggerValue
            className="w-40"
            placeholder={t('accessReaders.deviceUsers.filter.status')}
          />
          <Combobox.Content>
            <Combobox.Input placeholder={t('accessReaders.deviceUsers.filter.status')} />
            <Combobox.Empty>{t('common.noResults')}</Combobox.Empty>
            <Combobox.List>
              {(item: StatusFilterOption) => (
                <Combobox.Item key={item.value} value={item}>
                  {item.label}
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Content>
        </Combobox>

        <Button
          variant="secondary"
          size="sm"
          icon={<ArrowClockwiseIcon size={16} />}
          onClick={() => void load()}
        >
          {t('accessReaders.deviceUsers.refresh')}
        </Button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && deviceUsers.length === 0 && (
        <Empty
          icon={<UsersIcon size={48} className="text-kumo-inactive" />}
          title={t('accessReaders.deviceUsers.empty')}
        />
      )}

      {!loading && !error && deviceUsers.length > 0 && filteredDeviceUsers.length === 0 && (
        <Empty
          icon={<UsersIcon size={48} className="text-kumo-inactive" />}
          title={t('common.noResultsForFilter')}
        />
      )}

      {!loading && !error && filteredDeviceUsers.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>{t('accessReaders.deviceUsers.photo')}</Table.Head>
                    <Table.Head>{t('accessReaders.deviceUsers.name')}</Table.Head>
                    <Table.Head>{t('accessReaders.deviceUsers.status')}</Table.Head>
                    <Table.Head>{t('accessReaders.actions')}</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredDeviceUsers.map((du) => {
                    const photoUrl = photos[du.deviceUserId]
                    return (
                      <Table.Row
                        key={du.deviceUserId}
                        data-testid={`device-user-row-${du.deviceUserId}`}
                      >
                        <Table.Cell>
                          {photoUrl ? (
                            <img
                              src={photoUrl}
                              alt={t('accessReaders.deviceUsers.photoAlt')}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-kumo-base text-kumo-subtle">
                              {du.deviceName.slice(0, 1).toUpperCase()}
                            </div>
                          )}
                        </Table.Cell>
                        <Table.Cell>{du.deviceName}</Table.Cell>
                        <Table.Cell>
                          {du.mapped ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="success">
                                {t('accessReaders.deviceUsers.mapped')}
                              </Badge>
                              {coworkerNameFor(selections[du.deviceUserId])}
                            </div>
                          ) : (
                            <Combobox
                              label={t('accessReaders.deviceUsers.coworker')}
                              items={sortedCoworkers}
                              value={
                                sortedCoworkers.find((c) => c.id === selections[du.deviceUserId]) ??
                                null
                              }
                              onValueChange={(v) => {
                                const opt = v as CoworkerOption | null
                                setSelections((prev) => ({
                                  ...prev,
                                  [du.deviceUserId]: opt?.id ?? '',
                                }))
                              }}
                              isItemEqualToValue={(a, b) => a.id === b.id}
                              disabled={mappingId === du.deviceUserId}
                            >
                              <Combobox.TriggerValue
                                className="w-48"
                                placeholder={t('accessReaders.deviceUsers.selectPlaceholder')}
                              />
                              <Combobox.Content>
                                <Combobox.Input
                                  placeholder={t(
                                    'accessReaders.deviceUsers.comboboxSearchPlaceholder',
                                  )}
                                />
                                <Combobox.Empty>{t('common.noResults')}</Combobox.Empty>
                                <Combobox.List>
                                  {(item: CoworkerOption) => (
                                    <Combobox.Item key={item.id} value={item}>
                                      {coworkerLabel(item)}
                                    </Combobox.Item>
                                  )}
                                </Combobox.List>
                              </Combobox.Content>
                            </Combobox>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {!du.mapped && (
                            <>
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={
                                  !selections[du.deviceUserId] || mappingId === du.deviceUserId
                                }
                                loading={mappingId === du.deviceUserId}
                                onClick={() => void handleMap(du.deviceUserId)}
                              >
                                {t('accessReaders.deviceUsers.map')}
                              </Button>
                              {rowErrors[du.deviceUserId] && (
                                <div className="mt-1 text-xs text-kumo-danger">
                                  {rowErrors[du.deviceUserId]}
                                </div>
                              )}
                            </>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table>
            </div>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}

DeviceUsersPage.displayName = 'DeviceUsersPage'
